import { clientMessages, type ClientMessageKey } from "@/i18n/client-messages";
import { getBrowserLocale, type Locale } from "@/i18n/locales";

export type User = { id: string; email: string; display_name: string };
export type PublicGame = {
  id: string;
  title: string;
  description: string | null;
  instructions: string | null;
  creator_name: string;
  published_at: string;
  updated_at: string;
  play_url: string;
};
export type GamePage = PublicGame & { iframe_url: string };
export type GameList = { items: PublicGame[]; next_cursor: string | null };
export type ApiKey = { id: string; name: string; prefix: string; last_four: string; created_at: string; last_used_at: string | null; revoked_at: string | null };
export type OwnedGame = {
  id: string;
  title: string;
  description: string | null;
  status: string;
  blocked: boolean;
  play_url: string;
  current_deployment_id: string | null;
  latest_deployment: { id: string; status: string; error_code: string | null; error_message: string | null; created_at: string } | null;
  created_at: string;
  updated_at: string;
};
export type Usage = { requests_today: number; requests_limit: number; requests_remaining: number; storage_bytes: number; storage_reserved_bytes: number; storage_limit_bytes: number; as_of: string; reset_at: string };

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

export async function parseApiResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = typeof payload?.error?.message === "string" ? payload.error.message : "Request failed";
    const code = typeof payload?.error?.code === "string" ? payload.error.code : undefined;
    throw new ApiError(message, response.status, code);
  }
  if (!payload || typeof payload !== "object" || !("data" in payload)) throw new ApiError("Invalid server response", 502, "invalid_response");
  return payload.data as T;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    const headers = new Headers(init.headers);
    if (init.body) headers.set("Content-Type", "application/json");
    const response = await fetch(`/api/backend${path}`, { ...init, headers, credentials: "same-origin", cache: "no-store" });
    return await parseApiResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError("Unable to reach the server", 0, "network_error");
  }
}
// Translate stable error codes, rather than exposing raw backend messages or internals.
const errorKeys: Record<string, ClientMessageKey> = {
  invalid_response: "errorResponse", network_error: "errorNetwork",
  invalid_input: "errorInput", invalid_cursor: "errorInput", invalid_limit: "errorInput",
  email_unavailable: "errorEmail", invalid_login_token: "errorLoginToken",
  account_unavailable: "errorAccount", account_blocked: "errorAccount",
  auth_required: "errorLoginRequired", recent_login_required: "errorRecentLogin",
  invalid_api_key: "errorApiKey", api_key_limit: "errorKeyLimit",
  not_found: "errorNotFound", rate_limit_exceeded: "errorRateLimit", report_limit: "errorReportLimit",
  origin_not_allowed: "errorForbidden", game_blocked: "errorBlocked",
  storage_limit_exceeded: "errorStorage", upload_expired: "errorUploadExpired",
  file_too_large: "errorFileLimit", expanded_size_limit: "errorFileLimit", too_many_files: "errorFileLimit",
  missing_index: "errorMissingIndex", sensitive_file: "errorSensitiveFile",
  unsafe_path: "errorZip", unsupported_file: "errorZip", unsupported_entry: "errorZip",
  unsupported_zip: "errorZip", duplicate_path: "errorZip", zip_corrupt: "errorZip", invalid_zip: "errorZip",
};
export function errorMessage(error: unknown, locale: Locale = getBrowserLocale()): string {
  const t = clientMessages[locale];
  if (error instanceof ApiError) {
    const key = error.code ? errorKeys[error.code.toLowerCase()] : undefined;
    if (key) return t[key];
    if (error.status === 0) return t.errorNetwork;
    if (error.status === 401) return t.errorLoginRequired;
    if (error.status === 403) return t.errorForbidden;
    if (error.status === 404) return t.errorNotFound;
    if (error.status === 429) return t.errorRateLimit;
    if (error.status === 400 || error.status === 422) return t.errorInput;
  }
  return t.errorRequest;
}
export function deploymentErrorMessage(code: string | null, locale: Locale = getBrowserLocale()): string {
  return clientMessages[locale][code ? errorKeys[code.toLowerCase()] ?? "errorDeployment" : "errorDeployment"];
}
export function formatDate(value: string | null, locale: Locale = getBrowserLocale()): string {
  if (!value) return clientMessages[locale].never;
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? "—" : date.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}
export function formatBytes(bytes: number, locale: Locale = getBrowserLocale()): string {
  if (bytes < 1024) return `${bytes.toLocaleString(locale)} B`;
  const unit = bytes < 1024 ** 2 ? 1 : bytes < 1024 ** 3 ? 2 : 3;
  return `${(bytes / 1024 ** unit).toLocaleString(locale, { minimumFractionDigits: unit === 3 ? 2 : 1, maximumFractionDigits: unit === 3 ? 2 : 1 })} ${["B", "KiB", "MiB", "GiB"][unit]}`;
}
